import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import LiveTvIcon from '@material-ui/icons/LiveTv';

import { FeedsCarouselStream, FeedsCarouselViewport } from '../types';

import LinkIcon from './icons/Link';
import styles from './index.css';


const INIT_DELAY = 1000;

type Props = {
  isVisible?: boolean;
  stream: FeedsCarouselStream;
  viewport: FeedsCarouselViewport;
}

const Item: FC<Props> = (props) => {
  const { isVisible, stream, viewport } = props;

  const {
    defaultImageLink,
    hlsPreviewUrl,
    snapshotImageLink,
    username,
  } = stream;

  const thumbnail = snapshotImageLink || defaultImageLink;
  const [playing, setPlaying] = useState<boolean>(false);

  const container = useRef<HTMLAnchorElement>(null);
  const isLoaded = useRef<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hls = useRef<Hls>(null);

  const isInViewport = useCallback((): boolean => {
    if (!isVisible) {
      return false;
    }

    const el = container.current;
    if (!el) {
      return false;
    }

    const { scroll, height } = viewport;
    const { offsetTop, offsetHeight } = el;

    if (scroll > offsetTop) {
      return false;
    }

    if (scroll + height < offsetTop + offsetHeight) {
      return false;
    }

    return true;
  }, [viewport, isVisible]);

  const initPreview = useCallback(async() => {
    if (!hlsPreviewUrl || isLoaded.current || !isVisible) {
      return;
    }

    const videoNode = videoRef.current;
    if (!videoNode) {
      return;
    }

    const isHlsSupported = Hls.isSupported();

    if (!isHlsSupported && videoNode.canPlayType('application/vnd.apple.mpegurl')) {
      videoNode.src = hlsPreviewUrl;

      try {
        await videoNode.play();
        isLoaded.current = true;
        setPlaying(true);
      } catch (err) {
        // nothing to do here;
      }

      return;
    }

    if (!isHlsSupported) {
      return;
    }

    isLoaded.current = true;

    hls.current = new Hls({ startLevel: 0, testBandwidth: false });
    hls.current.startLevel = 0;
    hls.current.nextLevel = 0;
    hls.current.nextAutoLevel = 0;
    hls.current.loadSource(hlsPreviewUrl);
    hls.current.attachMedia(videoNode);
    hls.current.on(Hls.Events.MANIFEST_PARSED, async() => {
      try {
        await videoNode.play();
        setPlaying(true);
      } catch {
        // nothing to do here
      }
    });
  }, [hlsPreviewUrl, isVisible]);

  useEffect(() => {
    if (!isInViewport()) {
      if (!videoRef.current.paused) {
        hls.current?.stopLoad?.();
        videoRef.current.pause();
      }

      return;
    }

    if (isLoaded.current && playing && (videoRef.current.paused || videoRef.current.currentTime === 0)) {
      try {
        hls.current?.startLoad?.();
        videoRef.current.play();
      } catch {
        // nothing to do here
      }

      return;
    }

    setTimeout(initPreview, INIT_DELAY);
  }, [viewport, isInViewport, initPreview, playing]);

  useEffect(() => () => {
    hls.current?.destroy?.();
  }, []);

  return (
    <a
      ref={container}
      className={styles.container}
      href={stream.routerUrl}
      rel="noopener noreferrer"
      target="_blank"
    >
      <div className={styles.title}>
        {username}
      </div>

      <div className={styles.imageContainer}>
        <div className={styles.overlay} >
          <LinkIcon className={styles.overlayIcon} />
        </div>

        {!playing && (
          <img
            alt={username}
            className={styles.image}
            src={thumbnail}
          />
        )}

        <video
          ref={videoRef}
          autoPlay={true}
          className={styles.video}
          muted={true}
          playsInline={true}
        />

        <div className={styles.iconsWrapper}>
          <LiveTvIcon className={styles.iconContainer} />
        </div>
      </div>
    </a>
  );
};

Item.defaultProps = { isVisible: false };

export default Item;
